import React, { useMemo, useState } from 'react'
// import axios from 'axios'
import { useForm, SubmitHandler } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { Input, Button, Link } from '@mui/material'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling'
import { StandardModal, PresetDateRange } from 'components'
import { IDateRange } from 'components/PresetDateRange'
import AlertSnackbar, {
    alertSnackbarContentProps,
} from 'components/AlertSnackbar'
import idDirectory from '../idAttributes'

type TCaseUpdaterInputs = {
    cases: File | null
    created_date: string | null
}

interface ICaseUpdaterProps {
    openCaseUpdater: boolean
    onClose: () => void
    setOpenAlertDialog: (status: boolean) => void
    setAlertSnackbarMainProps: (value: alertSnackbarContentProps) => void
    setAlertSnackbarMainOpen: (status: boolean) => void
}

export const CaseUpdater: React.FC<ICaseUpdaterProps> = ({
    openCaseUpdater,
    onClose,
    setOpenAlertDialog
    // setAlertSnackbarMainProps,
    // setAlertSnackbarMainOpen,
}) => {
    const { clientTheme } = useThemeStyling()

    const [alertSnackbarOpen, setAlertSnackbarOpen] = useState<boolean>(false)
    const [
        alertSnackbarProps,
        // setAlertSnackbarProps,
    ] = useState<alertSnackbarContentProps>({})

    const defaultDateRange: IDateRange = {
        to: '',
        from: '',
    }

    const validationSchema = Yup.object().shape({
        cases: Yup.mixed().required(),
        created_date: Yup.string().required(),
    })

    const defaultValues = useMemo<TCaseUpdaterInputs>(() => {
        return {
            cases: null,
            created_date: null,
        }
    }, [])

    const {
        reset,
        watch,
        handleSubmit,
        setValue,
        formState: { isSubmitting, isValid, isDirty },
        // TODO: Known React-Hook-Form typescript issue - Should be resolved with RHF's v8. Once resolved, need to switch typescript's "any" to "TCaseUpdaterInputs".
    } = useForm<any>({
        defaultValues: defaultValues,
        mode: 'onChange',
        resolver: yupResolver(validationSchema),
    })

    const onSubmit: SubmitHandler<TCaseUpdaterInputs> = (values) => {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve({ message: '' })
            }, 750)
        }).finally(() => {
            handleOnClose()
            setOpenAlertDialog(true)
        })
    }

    const handleSelectedValue = (key: 'cases' | 'created_date', value: any) => {
        setValue(key, value, {
            shouldValidate: true,
            shouldDirty: true,
        })
    }

    const handleCSVUpload = (uploadedFile: File) => {
        if (!uploadedFile) return handleSelectedValue('cases', null)
        handleSelectedValue('cases', uploadedFile)
    }

    const handleOnClose = () => {
        onClose()
        reset()
    }

    return (
        <StandardModal
            openModal={openCaseUpdater}
            onModalClose={handleOnClose}
            modalHeaderTitle={'Case Updater'}
            maxModalWidth={'xs'}
            testId={'caseUpdater'}
            primaryBtnText={'Submit'}
            handlePrimaryActionBtn={handleSubmit(onSubmit)}
            inactivatePrimaryActionBtn={isSubmitting || !isDirty || !isValid}
            isLoadingPrimaryActionBtn={isSubmitting}
            handleSecondaryActionBtn={handleOnClose}
        >
            <div
                id={'chargebacksCaseUpdater'}
                style={{
                    fontSize: clientTheme.typography.body1.fontSize,
                    overflow: 'initial',
                }}
            >
                <div
                    id={idDirectory.caseUpdater.divRoot}
                    className={'emp-mainContainer'}
                >
                    <div className={'emp-inputContainer'}>
                        <label>
                            Created Date:
                            <span style={{ color: 'red' }}>*</span>
                        </label>
                        <div>
                            {' '}
                            <PresetDateRange
                                getDateRange={(date: string) => {
                                    handleSelectedValue('created_date', date)
                                }}
                                className={'emp-dateRangePicker'}
                                allowFutureDateSelection={false}
                                dateRange={defaultDateRange}
                                testId={'caseUpdater-createdDate'}
                                enableDateRangeForDialog
                            />
                        </div>
                    </div>

                    <div className={'emp-inputContainer'}>
                        <label>Columns Needed:</label>
                        <div>
                            <Link
                                id={idDirectory.caseUpdater.linkTemplate}
                                className={'emp-downloadTemplateLink'}
                                href={`${process.env.PUBLIC_URL}/caseupdater_template.csv`}
                                sx={{ color: clientTheme.secondaryLight }}
                            >
                                Download Template
                            </Link>
                        </div>
                    </div>

                    <div className={'emp-inputContainer'}>
                        <label>
                            Select File:
                            <span style={{ color: 'red' }}>*</span>
                        </label>
                        <div>
                            <label htmlFor="csv-file-input">
                                <Input
                                    id="csv-file-input"
                                    name="file"
                                    type="file"
                                    style={{ display: 'none' }}
                                    inputProps={{
                                        accept: '.csv',
                                    }}
                                    onChange={(e: any) => {
                                        handleCSVUpload(e.target.files[0])
                                    }}
                                />
                                <Button
                                    id={idDirectory.caseUpdater.btnUploadFile}
                                    color="secondary"
                                    variant="contained"
                                    component="span"
                                >
                                    {watch('cases') ? (
                                        <>
                                            <FontAwesomeIcon icon={faPlus} />{' '}
                                            &nbsp; Change File
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faPlus} />{' '}
                                            &nbsp; Choose File
                                        </>
                                    )}
                                </Button>
                                <div className={'emp-fileInputHelpText '}>
                                    Allowed type: CSV
                                </div>
                                {watch('cases') ? (
                                    <div className={'emp-selectedFile'}>
                                        Currently selected file: [
                                        {/* @ts-ignore */}
                                        {watch('cases').name}]
                                    </div>
                                ) : null}
                            </label>
                        </div>
                    </div>
                    <div className="emp-notificiation">
                        Maximum number of outcomes allowed to be updated in one
                        file is set to: 1000
                    </div>
                </div>
            </div>

            <AlertSnackbar
                content={alertSnackbarProps}
                open={alertSnackbarOpen}
                onClose={() => setAlertSnackbarOpen(false)}
                showCloseIcon
            />
        </StandardModal>
    )
}
